import { environment } from '@env/environment';
import { FRStep, StepType } from '@forgerock/javascript-sdk/lib-esm';
import { LoginInterfaceBaseComponent } from '@shared/lib/components/login-interface/login-interface-base.component';
import { LoginInterfaceStepStatus } from '@shared/lib/components/login-interface/login-interface-step-status';
import { FrLoginInterfaceMapper } from '@shared/lib/models/fr-login-interface-mapper';
import { FrLoginInterface } from '@shared/lib/models/fr-login-interface.enum';
import { LoginContext } from '@shared/lib/models/login-context';
import { LoginContextService } from '@shared/lib/services/login-context.service';
import { SharedLoginConstants } from '@shared/lib/shared-login.constants';
import { DynatraceService } from '@usana/ux/dynatrace';
import { UsanaMarketService } from '@usana/ux/markets';
import { LoginResponse, SsoAuthService, SsoLogoutService, SsoUser } from '@usana/ux/sso-auth';
import { TranslationService } from '@usana/ux/translations';
import { Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { LoginService } from '../../services/login.service';
import { LOGIN_INTERFACES } from '../../shared-login.tokens';

@Component({
  selector: 'login-page',
  templateUrl: 'login-page.component.html',
  styleUrls: ['login-page.component.scss'],
  standalone: false,
})
export class LoginPageComponent implements OnInit, OnDestroy, AfterViewInit {
  error: string;

  loggingIn: boolean;

  private enrollLink: string;
  private baoyingEnrollLink: string;
  private currentInterface: FrLoginInterface;
  private currentInterfaceComponent: ComponentRef<LoginInterfaceBaseComponent>;
  private currentInterfaceSubmitSub: Subscription;

  readonly destroy$ = new Subject<void>();

  @ViewChild('loginInterfaceVc', { read: ViewContainerRef })
  set LoginInterfaceVc(interfaceVc: ViewContainerRef) {
    this.setLoginInterface(interfaceVc);
  }

  private loginInterfaceVc: ViewContainerRef;

  private forcedLogin: boolean;

  private readonly redirectUriParam: string = 'redirectUri';
  private readonly weChatEnrollParam = 'wechat_register';

  constructor(
    private readonly ssoAuthService: SsoAuthService,
    private readonly loginService: LoginService,
    private readonly loginContextService: LoginContextService,
    private readonly usanaTranslationService: TranslationService,
    private readonly route: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ssoLogoutService: SsoLogoutService,
    private readonly usanaMarketService: UsanaMarketService,
    private readonly dynatraceService: DynatraceService,
    @Inject(LOGIN_INTERFACES) private readonly loginInterfaces: FrLoginInterfaceMapper,
  ) {}

  setLoginInterface(interfaceVc: ViewContainerRef): void {
    // yeah this is complicated, but we gotta do this so mocking is cleaner.
    this.loginInterfaceVc = interfaceVc;
  }

  ngOnInit(): void {
    this.error = '';
    this.enrollLink =
      `https://www.${environment.app.domain}/ux/cart/` +
      `${this.usanaMarketService.unsafeGetI18nInfo().current.phxMarketId}/page/pc-signup`;

    // get a uri param if we have one
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params: ParamMap) => {
      if (params.has(this.redirectUriParam)) {
        this.loginContextService.storeRedirectUri(params.get(this.redirectUriParam));
      }
    });

    // we only care about the first sub.
    this.route.queryParamMap
      .pipe(first()) // only the first
      .subscribe((queryParams: ParamMap) => {
        if (queryParams.get('sessionTimeout') === '1') {
          this.usanaTranslationService
            .translateAsPromise(SharedLoginConstants.TRANSLATION_PKG, 'sessionTimeout')
            .then((tVal: string) => {
              this.error = tVal;
            });
        }
        if (queryParams.get('switchUser') === '1') {
          this.forcedLogin = true;
        }

        if (queryParams.get('kiosk') === 'true') {
          this.loginContextService.setKioskMode(true);
        } else {
          this.loginContextService.setKioskMode(false);
        }

        // I wish we could check if we are wechat mini program here
        // but mini program doesn't load fast enough for me to use that
        // and it doesn't have any good callbacks for me to hook into
        if (this.isBaoying() && queryParams.get(this.weChatEnrollParam)) {
          this.baoyingEnrollLink = queryParams.get(this.weChatEnrollParam);
        }

        // need to put this inside the query param sub just so that we don't have any
        // possible race conditions.
        this.ssoAuthService
          .getUserSubject()
          .pipe(first()) // only the first
          .subscribe((user: SsoUser) => {
            if (user) {
              // if we are forcing a login we must login
              // if we are a mobile app login we do not redirect (can't read the cookies).
              if (!this.forcedLogin && !this.loginContextService.isMobileAppLogin()) {
                this.loginContextService.redirectPostLoginWithUser(user);
              }
              // if we are forced don't do anything
            }
          });
      });
  }

  ngAfterViewInit(): void {
    this.currentInterface = FrLoginInterface.NONE;

    this.loginService
      .getStepSub()
      .pipe(takeUntil(this.destroy$))
      .subscribe((frStep: FRStep) => {
        this.loggingIn = false;
        const frInterface = this.loginService.stepToInterface(frStep);
        this.currentInterface = frInterface;
        if (frInterface === FrLoginInterface.NONE || frStep.type.toString() === StepType.Step) {
          this.instantiateCurrentInterface();
        } else if (frInterface === FrLoginInterface.SUCCESS) {
          this.loggingIn = true;
          this.possibleLogoutCallback().then(() => {
            this.loginService
              .convertToken(frStep.payload.tokenId)
              .then((loginResponse: HttpResponse<LoginResponse>) => {
                if (!this.loginService.handleLoginResponse(loginResponse)) {
                  // error
                  this.usanaTranslationService
                    .translateAsPromise(SharedLoginConstants.TRANSLATION_PKG, 'tokenProblem')
                    .then((tVal: string) => {
                      this.error = tVal;
                    });
                }
              })
              .catch((err) => {
                this.loggingIn = false;
                this.currentInterfaceComponent.instance.handleError();
              });
          });
        } else {
          // have to cast any because the fr sdk doesn't actually state errorCode is a property
          if ((frStep.payload.detail as any).errorCode === '110') {
            this.instantiateInterface(FrLoginInterface.SESSION_EXPIRED);
          } else {
            this.error = this.errorStepToGlobalMessage();
            if (this.currentInterfaceComponent) {
              this.currentInterfaceComponent.instance.handleError();
            }
          }
        }
      });

    this.loginService
      .start()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (_error) => {
          this.loggingIn = false;

          this.usanaTranslationService
            .translateAsPromise(SharedLoginConstants.TRANSLATION_PKG, 'genericError')
            .then((tVal: string) => {
              this.error = tVal;
            });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.cleanupInterfaceSub();
  }

  showGoBack(): boolean {
    return (
      this.currentInterfaceComponent &&
      this.currentInterfaceComponent.instance.showGoBack() &&
      this.loginService.stepCount() > 1
    ); // > 1 because the first step is initiating the process so we can't go back to it
  }

  goToEnroll(event: any): void {
    event.preventDefault();
    if (this.baoyingEnrollLink) {
      // only allow this if we are miniProgram so that we don't have an open redirect
      if (this.loginContextService.determineContext() === LoginContext.WECHAT_MINI) {
        this.loginContextService.handleExternalRedirect(this.baoyingEnrollLink);
      }
    } else {
      this.loginContextService.handleExternalRedirect(this.enrollLink);
    }
  }

  showSignUp(): boolean {
    // we have to hide the sign up button on iOS since we don't allow users
    // to delete their account and Apple cares about your privacy ;)
    return (
      (!this.isBaoying() || this.baoyingEnrollLink) &&
      this.loginContextService.determineContext() !== LoginContext.IOS &&
      this.loginContextService.determineContext() !== LoginContext.ANDROID
    );
  }

  goBack(): void {
    this.loginService.previousStep(true);
  }

  isBaoying(): boolean {
    return environment.app.isBaoying;
  }

  private possibleLogoutCallback(): Promise<void> {
    // if we forced a login make sure we logout of whatever possible session we were in.
    if (this.forcedLogin) {
      this.dynatraceService.endSession();

      return this.ssoLogoutService.logout();
    } else {
      return Promise.resolve();
    }
  }

  private instantiateCurrentInterface(): void {
    this.instantiateInterface(this.currentInterface);
  }

  private instantiateInterface(frInterface: FrLoginInterface): void {
    this.loginInterfaceVc.clear();

    this.loggingIn = false;
    if (this.currentInterface === FrLoginInterface.NONE) {
      this.loggingIn = true;
    } else {
      // have to mark this as any so typescript stops popping off
      this.loginInterfaces.loadComponentForInterface(frInterface).then((component) => {
        this.cleanupInterfaceSub();

        this.currentInterfaceComponent = this.loginInterfaceVc.createComponent<LoginInterfaceBaseComponent>(component);
        this.currentInterfaceSubmitSub = this.currentInterfaceComponent.instance.stepSubmitted.subscribe(
          (stepStatus) => {
            if (stepStatus === LoginInterfaceStepStatus.START) {
              this.loggingIn = true;
              this.error = ''; // clear on submission
              // logout if we need to at this point
              // and then mark forcedLogin as false so we no longer logout
              this.possibleLogoutCallback().then(() => {
                this.forcedLogin = false;
              });
            } else if (stepStatus === LoginInterfaceStepStatus.FINISH) {
              this.loggingIn = false;
            }
          },
        );
      });
    }
    this.changeDetectorRef.detectChanges();
  }

  private cleanupInterfaceSub(): void {
    if (this.currentInterfaceSubmitSub) {
      this.currentInterfaceSubmitSub.unsubscribe();
      delete this.currentInterfaceSubmitSub;
    }
  }

  private errorStepToGlobalMessage(): string {
    // we don't push the failure to the step list so current step will be whatever our previous step is
    const previousStep = this.loginService.currentStep();
    switch (this.loginService.stepToInterface(previousStep)) {
      case FrLoginInterface.CHOICE:
        return 'error.invalidChoice';
      case FrLoginInterface.USERNAME_PASSWORD:
        return ''; // error is handled by the interface.
    }
    return 'error.generic';
  }
}
